import { useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { markShipmentCocCommentsRead } from '@/apis/shipment';
import { UPDATES_INBOX_QUERY_KEY } from '@/pages/Notifications/hooks/useUpdatesInbox';
import type {
  ShipmentActivityBell,
  ShipmentActivityPreviewItem,
  ShipmentRow,
} from '../types';
import type { ShipmentReportPage } from './useShipmentReport';

export interface MarkShipmentCommentReadVars {
  shipmentId: string;
  commentId?: string | null;
  mentionsMe?: boolean;
}

export interface CocCommentCreatedMeta {
  text: string;
  authorName?: string | null;
  timestamp: string;
}

interface UseShipmentActivityOptimisticUpdatesProps {
  token: string;
  shipmentsData: ShipmentRow[];
  filteredData: ShipmentRow[];
  setShipmentsData: Dispatch<SetStateAction<ShipmentRow[]>>;
  setFilteredData: Dispatch<SetStateAction<ShipmentRow[]>>;
}

export const getShipmentActivityRowId = (
  shipment: ShipmentRow
): string | null => {
  const id = shipment.id ?? shipment.shipment_id;
  return id == null ? null : String(id);
};

const getReadActivityPatch = (
  shipment: ShipmentRow,
  commentId?: string | null,
  mentionsMe?: boolean
): ShipmentRow => {
  if (!shipment.activity) return shipment;

  const unreadCount = shipment.activity.unread_count ?? 0;
  const mentionUnreadCount = shipment.activity.mention_unread_count ?? 0;
  const nextUnreadCount = commentId ? Math.max(0, unreadCount - 1) : 0;
  const nextMentionUnreadCount =
    commentId && mentionsMe
      ? Math.max(0, mentionUnreadCount - 1)
      : commentId
        ? mentionUnreadCount
        : 0;

  return {
    ...shipment,
    activity: {
      ...shipment.activity,
      has_unread: nextUnreadCount > 0,
      has_unread_mention: nextMentionUnreadCount > 0,
      unread_count: nextUnreadCount,
      mention_unread_count: nextMentionUnreadCount,
      preview: shipment.activity.preview.map((item) =>
        commentId && String(item.id) === commentId
          ? { ...item, mentions_me: false }
          : item
      ),
    },
  };
};

const patchShipmentRowsAsRead = (
  rows: ShipmentRow[],
  vars: MarkShipmentCommentReadVars
): ShipmentRow[] =>
  rows.map((row) =>
    getShipmentActivityRowId(row) === vars.shipmentId
      ? getReadActivityPatch(row, vars.commentId, vars.mentionsMe)
      : row
  );

const patchShipmentReportAsRead = (
  previous: InfiniteData<ShipmentReportPage> | undefined,
  vars: MarkShipmentCommentReadVars
): InfiniteData<ShipmentReportPage> | undefined => {
  if (!previous) return previous;

  return {
    ...previous,
    pages: previous.pages.map((page) => ({
      ...page,
      rows: patchShipmentRowsAsRead(page.rows, vars),
    })),
  };
};

const getCommentCreatedActivityPatch = (
  shipment: ShipmentRow,
  incrementCount = true,
  comment?: CocCommentCreatedMeta
): ShipmentRow => {
  const activity = shipment.activity ?? {
    comment_count: 0,
    has_unread: false,
    has_unread_mention: false,
    unread_count: 0,
    mention_unread_count: 0,
    preview: [],
    bell: null,
  };

  const optimisticPreviewItem: ShipmentActivityPreviewItem | null = comment
    ? {
        id: `optimistic-${comment.timestamp}`,
        author_name: comment.authorName,
        author: comment.authorName,
        timestamp: comment.timestamp,
        text: comment.text,
        mentions_me: false,
      }
    : null;

  return {
    ...shipment,
    activity: {
      ...activity,
      comment_count: incrementCount
        ? (activity.comment_count ?? 0) + 1
        : (activity.comment_count ?? 0),
      preview: optimisticPreviewItem
        ? [optimisticPreviewItem, ...(activity.preview ?? [])].slice(0, 3)
        : activity.preview,
    },
  };
};

const getShipmentBellActivityPatch = (
  shipment: ShipmentRow,
  bell: ShipmentActivityBell
): ShipmentRow => {
  const activity = shipment.activity ?? {
    comment_count: 0,
    has_unread: false,
    has_unread_mention: false,
    unread_count: 0,
    mention_unread_count: 0,
    preview: [],
    bell: null,
  };

  return {
    ...shipment,
    activity: {
      ...activity,
      bell,
    },
  };
};

const patchShipmentRowsById = (
  rows: ShipmentRow[],
  shipmentId: string,
  patchShipment: (shipment: ShipmentRow) => ShipmentRow
): ShipmentRow[] =>
  rows.map((row) =>
    getShipmentActivityRowId(row) === shipmentId ? patchShipment(row) : row
  );

const patchShipmentReportById = (
  previous: InfiniteData<ShipmentReportPage> | undefined,
  shipmentId: string,
  patchShipment: (shipment: ShipmentRow) => ShipmentRow
): InfiniteData<ShipmentReportPage> | undefined => {
  if (!previous) return previous;

  return {
    ...previous,
    pages: previous.pages.map((page) => ({
      ...page,
      rows: patchShipmentRowsById(page.rows, shipmentId, patchShipment),
    })),
  };
};

export const useShipmentActivityOptimisticUpdates = ({
  token,
  shipmentsData,
  filteredData,
  setShipmentsData,
  setFilteredData,
}: UseShipmentActivityOptimisticUpdatesProps) => {
  const queryClient = useQueryClient();

  const patchShipmentActivity = useCallback(
    (
      shipmentId: string,
      patchShipment: (shipment: ShipmentRow) => ShipmentRow
    ) => {
      queryClient.setQueriesData<InfiniteData<ShipmentReportPage>>(
        { queryKey: ['shipment-report'] },
        (previous) =>
          patchShipmentReportById(previous, shipmentId, patchShipment)
      );
      setShipmentsData((previous) =>
        patchShipmentRowsById(previous, shipmentId, patchShipment)
      );
      setFilteredData((previous) =>
        patchShipmentRowsById(previous, shipmentId, patchShipment)
      );
    },
    [queryClient, setFilteredData, setShipmentsData]
  );

  const markShipmentCommentReadMutation = useMutation({
    mutationFn: (vars: MarkShipmentCommentReadVars) =>
      markShipmentCocCommentsRead(
        token,
        vars.shipmentId,
        vars.commentId ? { comment_ids: [vars.commentId] } : undefined
      ),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['shipment-report'] });

      const previousReports = queryClient.getQueriesData<
        InfiniteData<ShipmentReportPage>
      >({
        queryKey: ['shipment-report'],
      });
      const previousShipmentsData = shipmentsData;
      const previousFilteredData = filteredData;

      queryClient.setQueriesData<InfiniteData<ShipmentReportPage>>(
        { queryKey: ['shipment-report'] },
        (previous) => patchShipmentReportAsRead(previous, vars)
      );
      setShipmentsData((previous) => patchShipmentRowsAsRead(previous, vars));
      setFilteredData((previous) => patchShipmentRowsAsRead(previous, vars));

      return {
        previousReports,
        previousShipmentsData,
        previousFilteredData,
      };
    },
    onError: (_error, _vars, context) => {
      context?.previousReports.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      if (context?.previousShipmentsData) {
        setShipmentsData(context.previousShipmentsData);
      }
      if (context?.previousFilteredData) {
        setFilteredData(context.previousFilteredData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UPDATES_INBOX_QUERY_KEY });
    },
  });

  const handleCocCommentCreated = useCallback(
    (shipmentId: string, comment?: CocCommentCreatedMeta) => {
      patchShipmentActivity(shipmentId, (shipment) =>
        getCommentCreatedActivityPatch(shipment, true, comment)
      );
    },
    [patchShipmentActivity]
  );

  const handleShipmentNotificationActivityChange = useCallback(
    (shipmentId: string, bell: ShipmentActivityBell) => {
      patchShipmentActivity(shipmentId, (shipment) =>
        getShipmentBellActivityPatch(shipment, bell)
      );
    },
    [patchShipmentActivity]
  );

  return {
    handleCocCommentCreated,
    handleShipmentNotificationActivityChange,
    markShipmentCommentReadMutation,
  };
};
